import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { getCart } from "@/lib/cart";
import { useAuth } from "@/context/AuthContext";

const inp = "w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 outline-none focus:border-rose-500 text-sm";

export default function Checkout() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items] = useState(getCart());
  const [quote, setQuote] = useState(null);
  const [cfg, setCfg] = useState({ guest_checkout: true });
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", state: "", zip: "", notes: "" });
  const [loyalty, setLoyalty] = useState([]);
  const [redeemInput, setRedeemInput] = useState({});
  const [applied, setApplied] = useState([]);
  const [promoInput, setPromoInput] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [gcCode, setGcCode] = useState("");
  const [gcPin, setGcPin] = useState("");
  const [gcPreview, setGcPreview] = useState(null); // { applied_amount, remaining_balance, ...}
  const [gcBusy, setGcBusy] = useState(false);

  const itemsBody = items.map((i) => ({ product_id: i.product_id, variation_id: i.variation_id, qty: i.qty, meta: i.meta }));

  useEffect(() => {
    if (!items.length) { nav("/cart"); return; }
    api.get("/marketplace/config").then((r) => setCfg(r.data)).catch(() => {});
    api.post("/marketplace/quote", { items: itemsBody })
      .then((r) => setQuote(r.data)).catch((e) => { toast.error(errMsg(e)); nav("/cart"); });
    api.post("/marketplace/loyalty-options", { items: itemsBody }).then((r) => setLoyalty(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  const applyPoints = async (opt, points) => {
    const redeem = applied.filter((r) => r.program_id !== opt.program_id);
    if (points > 0) redeem.push({ program_id: opt.program_id, points });
    try {
      const { data } = await api.post("/marketplace/quote", { items: itemsBody, redeem, promo_code: promoCode });
      setQuote(data);
      setApplied(redeem);
      if (points > 0) toast.success(`${points} ${opt.points_name} applied`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const applyPromo = async (code) => {
    const c = (code ?? promoInput).trim().toUpperCase();
    try {
      const { data } = await api.post("/marketplace/quote", { items: itemsBody, redeem: applied, promo_code: c });
      setQuote(data);
      setPromoCode(c);
      setPromoInput(c);
      if (c) toast.success(`Code ${c} applied`);
    } catch (e) {
      toast.error(errMsg(e));
      setPromoCode("");
    }
  };

  const removePromo = async () => {
    try {
      const { data } = await api.post("/marketplace/quote", { items: itemsBody, redeem: applied });
      setQuote(data); setPromoCode(""); setPromoInput("");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const applyGiftCard = async () => {
    if (!gcCode.trim() || !gcPin.trim()) { toast.error("Enter code and PIN"); return; }
    setGcBusy(true);
    try {
      const totalNow = Number(quote?.total || 0);
      const { data } = await api.post("/gift-cards/preview", {
        code: gcCode.trim(), pin: gcPin.trim(), order_total: totalNow,
        seller_id: quote?.lines?.[0]?.seller_id || "",
      });
      setGcPreview(data);
      toast.success(`Gift card applied — $${data.applied_amount.toFixed(2)}`);
    } catch (e) { toast.error(errMsg(e)); setGcPreview(null); }
    finally { setGcBusy(false); }
  };
  const removeGiftCard = () => { setGcPreview(null); setGcCode(""); setGcPin(""); };

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
  }, [user]);

  const needsShipping = (quote?.lines || []).some((l) => l.shipping > 0 || l.product_type === "physical_merch");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pay = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email are required");
    if (needsShipping && (!form.address.trim() || !form.city.trim())) return toast.error("Shipping address is required");
    setBusy(true);
    try {
      const { data } = await api.post("/marketplace/checkout", {
        items: itemsBody,
        redeem: applied,
        promo_code: promoCode,
        gift_card_code: gcPreview ? gcCode.trim() : "",
        gift_card_pin: gcPreview ? gcPin.trim() : "",
        buyer: { name: form.name, email: form.email, phone: form.phone },
        shipping_address: needsShipping ? { address: form.address, city: form.city, state: form.state, zip: form.zip } : {},
        notes: form.notes, origin_url: window.location.origin,
      });
      if (data.gift_card_covered) {
        // Gift card covered full amount → success page has no Stripe session.
        toast.success("Order paid entirely with your gift card 🎉");
        window.location.href = data.url;
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  if (!quote) return <div className="py-40 flex justify-center"><Loader2 className="animate-spin text-rose-500" /></div>;

  return (
    <div className="px-6 md:px-12 lg:px-20 py-12 min-h-screen" data-testid="checkout-page">
      <h1 className="font-display font-extrabold text-3xl md:text-4xl mb-8">Checkout</h1>
      {!user && !cfg.guest_checkout && (
        <div className="bg-amber-500/10 border border-amber-500/40 text-amber-300 rounded-xl px-4 py-3 text-sm mb-6">
          Please <Link to="/login" className="underline font-bold">log in</Link> to complete your purchase.
        </div>
      )}
      {!user && cfg.guest_checkout && (
        <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm mb-6 text-zinc-300">
          Checking out as a guest. <Link to="/login" className="text-rose-400 font-bold">Log in</Link> to track orders, tickets and memberships in your dashboard.
        </div>
      )}
      <div className="grid lg:grid-cols-3 gap-8 items-start">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-[#111] border border-white/10 rounded-2xl p-6">
            <h2 className="font-display font-bold text-lg mb-4">Your details</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <input className={inp} placeholder="Full name *" value={form.name} onChange={set("name")} data-testid="co-name" />
              <input className={inp} placeholder="Email *" type="email" value={form.email} onChange={set("email")} data-testid="co-email" />
              <input className={inp} placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="co-phone" />
            </div>
          </div>
          {needsShipping && (
            <div className="bg-[#111] border border-white/10 rounded-2xl p-6">
              <h2 className="font-display font-bold text-lg mb-4">Shipping address</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <input className={`${inp} sm:col-span-2`} placeholder="Street address *" value={form.address} onChange={set("address")} data-testid="co-address" />
                <input className={inp} placeholder="City *" value={form.city} onChange={set("city")} data-testid="co-city" />
                <div className="grid grid-cols-2 gap-3">
                  <input className={inp} placeholder="State" value={form.state} onChange={set("state")} data-testid="co-state" />
                  <input className={inp} placeholder="ZIP" value={form.zip} onChange={set("zip")} data-testid="co-zip" />
                </div>
              </div>
            </div>
          )}
          <div className="bg-[#111] border border-white/10 rounded-2xl p-6">
            <h2 className="font-display font-bold text-lg mb-4">Order notes</h2>
            <textarea className={`${inp} min-h-[80px]`} placeholder="Anything the seller should know? (optional)" value={form.notes} onChange={set("notes")} data-testid="co-notes" />
          </div>
          <div className="bg-[#111] border border-white/10 rounded-2xl p-6">
            <h2 className="font-display font-bold text-lg mb-4">Promo code</h2>
            {quote.promo ? (
              <div className="flex items-center justify-between gap-3 bg-emerald-500/10 border border-emerald-500/40 rounded-xl px-4 py-3" data-testid="co-promo-applied">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-emerald-300 font-mono">{quote.promo.code}</p>
                  <p className="text-xs text-zinc-300">{quote.promo.discount_type === "percent" ? `${quote.promo.discount_value}% off` : `$${Number(quote.promo.discount_value).toFixed(2)} off`} — you save ${Number(quote.promo.discount).toFixed(2)}</p>
                </div>
                <button onClick={removePromo} className="text-xs text-zinc-300 hover:text-rose-400 font-semibold" data-testid="co-promo-remove">Remove</button>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2">
                <input className={`${inp} uppercase font-mono tracking-wide`} placeholder="Have a code? Enter it here" value={promoInput} onChange={(e) => setPromoInput(e.target.value)} data-testid="co-promo-input"
                  onKeyDown={(e) => e.key === "Enter" && applyPromo()} />
                <button onClick={() => applyPromo()} disabled={!promoInput.trim()} className="bg-rose-500 hover:bg-rose-600 disabled:opacity-40 font-bold px-6 py-2.5 rounded-lg text-sm shrink-0" data-testid="co-promo-apply">Apply code</button>
              </div>
            )}
          </div>
          <div className="bg-[#111] border border-white/10 rounded-2xl p-6" data-testid="co-gift-card">
            <h2 className="font-display font-bold text-lg mb-1">Have a gift card?</h2>
            <p className="text-xs text-zinc-500 mb-3">Merchant and Universal cards use the same field. Balance applies up to the order total; the rest is paid by card.</p>
            {gcPreview ? (
              <div className="flex items-center justify-between gap-3 bg-rose-500/10 border border-rose-500/40 rounded-xl px-4 py-3" data-testid="co-gift-card-applied">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-rose-300 font-mono">{gcPreview.code}</p>
                  <p className="text-xs text-zinc-300">Applied ${gcPreview.applied_amount.toFixed(2)} · Balance after ${gcPreview.remaining_balance.toFixed(2)} · Remaining due ${gcPreview.remaining_due.toFixed(2)}</p>
                </div>
                <button onClick={removeGiftCard} className="text-xs text-zinc-300 hover:text-rose-400 font-semibold" data-testid="co-gift-card-remove">Remove</button>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2">
                <input className={`${inp} uppercase font-mono tracking-wide sm:flex-1`} placeholder="GCV-XXXX-XXXX-XXXX" value={gcCode} onChange={(e) => setGcCode(e.target.value.toUpperCase())} data-testid="co-gift-card-code" />
                <input className={`${inp} font-mono tracking-widest sm:w-28`} placeholder="PIN" value={gcPin} onChange={(e) => setGcPin(e.target.value)} data-testid="co-gift-card-pin" />
                <button onClick={applyGiftCard} disabled={gcBusy || !gcCode.trim() || !gcPin.trim()} className="bg-rose-500 hover:bg-rose-300 text-black disabled:opacity-40 font-bold px-6 py-2.5 rounded-lg text-sm shrink-0" data-testid="co-gift-card-apply">
                  {gcBusy ? "…" : "Apply card"}
                </button>
              </div>
            )}
          </div>
          {loyalty.length > 0 && (
            <div className="bg-gradient-to-r from-rose-500/10 to-amber-500/5 border border-rose-500/30 rounded-2xl p-6" data-testid="co-loyalty">
              <h2 className="font-display font-bold text-lg mb-1">Gulf Coast VIP Rewards</h2>
              {loyalty.map((opt) => {
                const cur = applied.find((r) => r.program_id === opt.program_id);
                return (
                  <div key={opt.program_id} className="mt-3 pt-3 border-t border-white/5 first:border-0 first:pt-0">
                    <p className="text-sm font-bold flex items-center gap-2">{opt.program_name}
                      {opt.is_global && <span className="bg-rose-500/15 text-rose-400 border border-rose-500/40 text-[9px] font-black tracking-wide px-1.5 py-0.5 rounded-full" data-testid="co-global-badge">PLATFORM-WIDE</span>}
                    </p>
                    {opt.earn_estimate > 0 && <p className="text-xs text-amber-300 mt-0.5">You'll earn ~{opt.earn_estimate} {opt.points_name} on this purchase</p>}
                    {opt.member ? (
                      opt.max_points >= opt.min_points ? (
                        <div className="flex items-center gap-2 mt-2">
                          <input type="number" min={0} max={opt.max_points} placeholder={`Points (${opt.min_points}-${opt.max_points})`}
                            value={redeemInput[opt.program_id] ?? ""} onChange={(e) => setRedeemInput({ ...redeemInput, [opt.program_id]: e.target.value })}
                            className="w-44 bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-rose-500" data-testid={`co-points-input-${opt.program_id}`} />
                          <button onClick={() => applyPoints(opt, Number(redeemInput[opt.program_id] || 0))}
                            className="text-xs font-bold bg-rose-500 hover:bg-rose-600 px-3.5 py-2 rounded-full" data-testid={`co-points-apply-${opt.program_id}`}>
                            {cur ? "Update" : "Apply"}
                          </button>
                          {cur && <button onClick={() => { setRedeemInput({ ...redeemInput, [opt.program_id]: "" }); applyPoints(opt, 0); }} className="text-xs text-zinc-400 hover:text-rose-400">Remove</button>}
                          <span className="text-xs text-zinc-400">You have <b className="text-emerald-300">{opt.available}</b> {opt.points_name}</span>
                        </div>
                      ) : <p className="text-xs text-zinc-500 mt-1">You have {opt.available} {opt.points_name} — minimum {opt.min_points} needed to redeem here.</p>
                    ) : (
                      <p className="text-xs text-zinc-400 mt-1">{user ? "Join this program from the seller's profile to redeem points." : "Log in and join to earn & redeem points."}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="bg-[#111] border border-white/10 rounded-2xl p-6 lg:sticky lg:top-24" data-testid="checkout-summary">
          <h2 className="font-display font-bold text-lg mb-4">Order summary</h2>
          <div className="space-y-2 mb-4 max-h-56 overflow-y-auto">
            {quote.lines.map((l) => (
              <div key={`${l.product_id}|${l.variation_name || ""}|${l.qty}`} className="flex justify-between text-sm text-zinc-300 gap-2">
                <span className="line-clamp-1">{l.qty} × {l.product_name}{l.variation_name ? ` (${l.variation_name})` : ""}</span>
                <span className="shrink-0">${l.line_total.toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="space-y-2 text-sm pt-3 border-t border-white/10">
            <div className="flex justify-between text-zinc-300"><span>Subtotal</span><span>${quote.subtotal.toFixed(2)}</span></div>
            {quote.shipping > 0 && <div className="flex justify-between text-zinc-300"><span>Shipping</span><span>${quote.shipping.toFixed(2)}</span></div>}
            {quote.buyer_fee > 0 && <div className="flex justify-between text-zinc-300"><span>Service fee</span><span>${quote.buyer_fee.toFixed(2)}</span></div>}
            {quote.loyalty_discount > 0 && <div className="flex justify-between text-emerald-300"><span>Rewards discount</span><span data-testid="co-loyalty-discount">-${quote.loyalty_discount.toFixed(2)}</span></div>}
            {quote.promo_discount > 0 && <div className="flex justify-between text-emerald-300"><span>Promo {quote.promo?.code ? `· ${quote.promo.code}` : ""}</span><span data-testid="co-promo-discount">-${quote.promo_discount.toFixed(2)}</span></div>}
            {quote.tax > 0 && <div className="flex justify-between text-zinc-300"><span>Tax</span><span>${quote.tax.toFixed(2)}</span></div>}
            <div className="flex justify-between font-bold text-base pt-3 border-t border-white/10"><span>Total</span><span data-testid="co-total">${Math.max(0, quote.total - (gcPreview?.applied_amount || 0)).toFixed(2)}</span></div>
            {gcPreview && gcPreview.applied_amount > 0 && (
              <div className="flex justify-between text-rose-300 text-xs" data-testid="co-gc-summary">
                <span>Gift card</span><span>-${gcPreview.applied_amount.toFixed(2)}</span>
              </div>
            )}
          </div>
          <button onClick={pay} disabled={busy || (!user && !cfg.guest_checkout)} data-testid="co-pay-btn"
            className="w-full mt-6 flex items-center justify-center gap-2 bg-rose-500 hover:bg-rose-600 disabled:opacity-40 font-bold py-3.5 rounded-full transition-colors">
            {busy ? <Loader2 className="animate-spin" size={17} /> : <Lock size={15} />} Pay ${quote.total.toFixed(2)}
          </button>
          <p className="text-[11px] text-zinc-500 mt-3 text-center">Secure payment powered by Stripe. By paying you accept the marketplace and refund policies.</p>
        </div>
      </div>
    </div>
  );
}
